import axios from 'axios'
import md5 from 'js-md5'

// 与 website-vite 一致的授权方式：通过后端 `/v1/stock/token` 获取缓存的 Bearer
const API_BASE = process.env.VUE_APP_BASE_API || ''
const TOKEN_STORAGE_KEY = 'stock_admin_token'
const EXPIRE_STORAGE_KEY = 'stock_admin_token_expire'

let cachedToken = null
let tokenExpireAt = 0
let fetchingToken = null
let serverOffsetMs = 0

try {
  const savedToken = typeof window !== 'undefined' ? window.localStorage.getItem(TOKEN_STORAGE_KEY) : null
  const savedExpire = typeof window !== 'undefined' ? window.localStorage.getItem(EXPIRE_STORAGE_KEY) : null
  if (savedToken && savedExpire) {
    const exp = Number(savedExpire)
    if (Number.isFinite(exp) && Date.now() < exp - 60_000) {
      cachedToken = savedToken
      tokenExpireAt = exp
    }
  }
} catch {}

const authClient = axios.create({ baseURL: API_BASE })

async function fetchToken() {
  const now = Date.now()
  if (cachedToken && tokenExpireAt && now < tokenExpireAt - 60_000) return cachedToken
  if (!fetchingToken) {
    fetchingToken = (async () => {
      const appid = process.env.VUE_APP_APPID
      const apikey = process.env.VUE_APP_APIKEY
      const referer = typeof window !== 'undefined' ? window.location.origin : ''
      const ts = Math.floor(Date.now() / 1000)
      const key = md5(String(appid) + String(apikey) + String(referer) + String(ts))
      const resp = await authClient.get('/v1/stock/token', {
        headers: { appid, key, referer, ts }
      })
      const data = resp.data?.data || resp.data || {}
      const token = String(data.token || '')
      if (!token) throw new Error('授权失败：未返回 token')
      cachedToken = token
      // 支持后端返回 expire_at（字符串时间），否则退化为 1 小时
      let expMs = now + 60 * 60 * 1000
      const expireStr = String(data.expire_at || '')
      if (expireStr) {
        const dt = expireStr.replace(/-/g, '/').replace(' ', 'T')
        const ms = Date.parse(dt)
        if (Number.isFinite(ms)) expMs = ms
      }
      tokenExpireAt = expMs
      const srvDate = (resp.headers && resp.headers['date']) || ''
      if (srvDate) {
        const ms = Date.parse(String(srvDate))
        if (Number.isFinite(ms)) serverOffsetMs = ms - Date.now()
      }
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(TOKEN_STORAGE_KEY, cachedToken)
          window.localStorage.setItem(EXPIRE_STORAGE_KEY, String(tokenExpireAt))
        }
      } catch {}
      return cachedToken
    })().finally(() => { fetchingToken = null })
  }
  return fetchingToken
}

function prefixApiPath(url) {
  const u = String(url || '')
  if (!u) return '/v1/api/'
  if (u.startsWith('http://') || u.startsWith('https://')) return u
  if (u.startsWith('/v1/api')) return u
  if (u.startsWith('/')) return '/v1/api' + u
  return '/v1/api/' + u
}

function genNonce(len = 16) {
  const bytes = new Uint8Array(len)
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < len; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
}

const stockClient = axios.create({ baseURL: API_BASE })

stockClient.interceptors.request.use(async (config) => {
  config.url = prefixApiPath(config.url)
  const token = await fetchToken()
  const ts = Math.floor((Date.now() + serverOffsetMs) / 1000).toString()
  const nonce = genNonce(16)
  const method = (config.method || 'get').toLowerCase()
  const isBodyMethod = method === 'post' || method === 'put' || method === 'patch'
  let body = ''
  if (isBodyMethod && config.data != null) {
    if (typeof config.data === 'string') {
      body = config.data
    } else {
      body = JSON.stringify(config.data)
      config.data = body
    }
  }
  const signature = md5(token + ts + nonce + body)
  const headers = config.headers || {}
  headers['Authorization'] = `Bearer ${token}`
  headers['X-Timestamp'] = ts
  headers['X-Nonce'] = nonce
  headers['X-Signature'] = signature
  if (isBodyMethod) headers['Content-Type'] = headers['Content-Type'] || 'application/json'
  config.headers = headers
  return config
})

stockClient.interceptors.response.use(res => res, async (error) => {
  const { response, config } = error || {}
  if (response && config && !config._retried) {
    const status = response.status
    if (status === 401 || status === 403) {
      cachedToken = null
      tokenExpireAt = 0
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(TOKEN_STORAGE_KEY)
          window.localStorage.removeItem(EXPIRE_STORAGE_KEY)
        }
      } catch {}
      config._retried = true
      return stockClient(config)
    }
  }
  return Promise.reject(error)
})

export default stockClient
